import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, Box, TextField, InputAdornment, Typography,
  CircularProgress, FormControl, InputLabel, Select, MenuItem, Chip, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { searchAPI } from '../../services/api';
import SearchResultItem from './SearchResultItem';

const DEBOUNCE_MS = 300;

/**
 * Projektübergreifende Recherche (Cmd/Ctrl+K).
 *
 * Gedacht für Abnahmen und strittige Punkte: „was hat der Kunde wann gesagt".
 * Deshalb sind Autor und Zeitraum hier erstklassige Filter und abgeschlossene
 * wie archivierte Tickets standardmäßig dabei – anders als im Board, das nur
 * den laufenden Betrieb zeigt.
 */
function GlobalSearchDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [authorId, setAuthorId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [includeArchived, setIncludeArchived] = useState(true);
  const [authors, setAuthors] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqSeq = useRef(0);

  useEffect(() => {
    if (!open) return;
    searchAPI.commentAuthors()
      .then((res) => setAuthors(res.data || []))
      .catch((err) => console.error('[SEARCH] authors error', err));
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(draft), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft]);

  const runSearch = useCallback(() => {
    const q = query.trim();
    if (!q) {
      reqSeq.current += 1;
      setData(null);
      setLoading(false);
      return;
    }
    const reqId = ++reqSeq.current;
    setLoading(true);
    searchAPI.tasks({
      q,
      scope: 'all',
      include_archived: includeArchived,
      ...(authorId ? { author_id: authorId } : {}),
      ...(dateFrom ? { from: dateFrom } : {}),
      ...(dateTo ? { to: dateTo } : {}),
      limit: 100,
    })
      .then((res) => { if (reqId === reqSeq.current) setData(res.data); })
      .catch((err) => {
        if (reqId !== reqSeq.current) return;
        console.error('[SEARCH] global search error', err);
        setData({ results: [], terms: [], counts: { total: 0 } });
      })
      .finally(() => { if (reqId === reqSeq.current) setLoading(false); });
  }, [query, authorId, dateFrom, dateTo, includeArchived]);

  useEffect(() => { runSearch(); }, [runSearch]);

  const openTask = (result) => {
    onClose();
    navigate(`/task/${result.task.id}`);
  };

  const results = data?.results || [];
  const filtersActive = Boolean(authorId || dateFrom || dateTo || !includeArchived);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { position: 'fixed', top: 60, m: 0, maxHeight: 'calc(100vh - 120px)' } }}
    >
      <DialogContent sx={{ p: 2 }}>
        <TextField
          autoFocus
          fullWidth
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Tickets und Kommentare durchsuchen – auch abgeschlossene und archivierte"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading ? <CircularProgress size={18} /> : <SearchIcon />}
              </InputAdornment>
            ),
          }}
        />

        {/* Recherche-Filter: wer, wann */}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Autor</InputLabel>
            <Select
              value={authorId}
              label="Autor"
              onChange={(e) => setAuthorId(e.target.value)}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              <MenuItem value="">Alle</MenuItem>
              {authors.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}{a.role === 'client' ? ' (Kunde)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small" type="date" label="von" value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
          />
          <TextField
            size="small" type="date" label="bis" value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
          />

          <Chip
            size="small"
            label="Archiv einbeziehen"
            color={includeArchived ? 'primary' : 'default'}
            variant={includeArchived ? 'filled' : 'outlined'}
            onClick={() => setIncludeArchived((v) => !v)}
          />
        </Box>

        {(authorId || dateFrom || dateTo) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Autor- und Zeitraumfilter beziehen sich auf Kommentare – Tickets ohne passenden
            Kommentar fallen damit heraus.
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {!query.trim() && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Suchbegriff eingeben. <b>#142</b> springt auf eine Ticketnummer, mehrere Wörter
            müssen alle vorkommen, <b>&quot;in Anführungszeichen&quot;</b> sucht die Wortfolge.
          </Typography>
        )}

        {query.trim() && !loading && results.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Keine Treffer{filtersActive ? ' mit diesen Filtern' : ''}.
          </Typography>
        )}

        {results.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary">
              {data.counts.total} Ticket{data.counts.total === 1 ? '' : 's'}
              {data.counts.comment_matches > 0
                && ` · ${data.counts.comment_matches} Fundstelle${data.counts.comment_matches === 1 ? '' : 'n'} in Kommentaren`}
              {data.truncated && ' · Anzeige gekürzt, Suche weiter eingrenzen'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {results.map((r) => (
                <SearchResultItem
                  key={r.task.id}
                  result={r}
                  terms={data.terms || []}
                  onOpen={openTask}
                  showProject
                />
              ))}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default GlobalSearchDialog;
